import axios from 'axios';
import { getKindeToken } from './tokenUtils';
import { AssetsConfig } from './AssetsConfig';

const API_BASE_URL = 'https://153r99o269.execute-api.us-east-1.amazonaws.com/test';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

const api2 = axios.create({
    baseURL: API_BASE_URL,
});

api.interceptors.request.use(
    async config => {
        try {
            const token = await getKindeToken();
            config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
            console.error('Failed to get token:', error);
        }
        return config;
    },
    error => Promise.reject(error)
);

const getMimeType = filename => {
    const extension = filename.split('.').pop().toLowerCase();
    const mimeTypes = {
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        gif: 'image/gif',
        pdf: 'application/pdf',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xls: 'application/vnd.ms-excel',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        zip: 'application/zip',
        glb: 'model/gltf-binary',
        gltf: 'model/gltf+json',
        txt: 'text/plain',
        json: 'application/json',
        mp4: 'video/mp4',
        mp3: 'audio/mpeg',
        // Add more mime types as needed
    };

    return mimeTypes[extension] || 'application/octet-stream';
};

export const ProjectService = {
    async getUser(userId) {
        try {
            const response = await api.get(`/users/${userId}`);
            return response.data;
        } catch (error) {
            console.error('Failed to get user', error);

            throw error;
        }
    },

    async updateUser(userId, body) {
        try {
            const response = await api.put(`/users/${userId}`, body);
            return response.data.data;
        } catch (error) {
            console.error('Failed to update user', error);

            throw error;
        }
    },

    async createUser(userId, body) {
        try {
            const response = await api.post(`/users/${userId}`, body);
            return response.data.data;
        } catch (error) {
            console.error('Failed to create new user', error);

            throw error;
        }
    },

    async getUserProjects(userId, retries = 3) {
        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                const response = await api.get(`/projects?userId=${userId}`);
                return response.data.data;
            } catch (error) {
                console.error(`Attempt ${attempt} to fetch projects failed`, error);
                if (error?.response?.status === 400 || error?.response?.status === 401) {
                    console.warn('Session expired. Logging out...');
                }
                if (attempt === retries) {
                    throw error;
                }
            }
        }
    },

    async getBusinessProjects() {
        try {
            const response = await api.get('/business/projects');
            return response.data.data;
        } catch (error) {
            console.error('Failed to fetch business projects', error);
            if (error?.response?.status === 403) {
                throw new Error('Unauthorized: Insufficient permissions');
            }
            throw error;
        }
    },

    async getBusinessCollections() {
        try {
            const response = await api.get('/business/collections');
            return response.data.data;
        } catch (error) {
            console.error('Failed to fetch business collections', error);
            if (error?.response?.status === 403) {
                throw new Error('Unauthorized: Insufficient permissions');
            }
            throw error;
        }
    },

    async getProjectById(projectId) {
        try {
            const response = await api.get(`/projects/${projectId}`);
            return response.data.data;
        } catch (error) {
            console.error('Failed to fetch project', error);

            throw error;
        }
    },

    async createProject(userId, name = null) {
        const newProject = {
            userId,
            name: name || `Kitchen Project ${(await this.getUserProjects(userId)).length + 1}`,
        };

        try {
            const response = await api.post('/projects', newProject);
            return response.data.data;
        } catch (error) {
            console.error('Failed to create project', error);

            throw error;
        }
    },

    async updateProject(projectId, updates) {
        try {
            const response = await api.put(`/projects/${projectId}`, updates);
            return response.data.data;
        } catch (error) {
            console.error('Failed to update project', error);

            throw error;
        }
    },

    async deleteProject(projectId) {
        try {
            await api.delete(`/projects/${projectId}`);
        } catch (error) {
            console.error('Failed to delete project', error);

            throw error;
        }
    },

    async addCompletedFlow(projectId, flowId) {
        try {
            const response = await api.post(`/projects/${projectId}/flows`, { flowName: flowId });
            return response.data.data;
        } catch (error) {
            console.error('Failed to add completed flow', error);

            throw error;
        }
    },

    async submitProject(projectId) {
        return this.updateProject(projectId, { status: 'submitted' });
    },

    async submitProjects(projectIds) {
        // Usage for single project
        // await submitProjects([projectId]);
        // Usage for multiple projects
        // await submitProjects([projectId1, projectId2, projectId3]);
        try {
            const response = await api.post('/projects/submit', { projectIds });
            return response.data.data;
        } catch (error) {
            console.error('Failed to submit projects', error);
            throw error;
        }
    },

    async updateProjectName(projectId, newName) {
        return this.updateProject(projectId, { name: newName });
    },

    async getPresignedUrl(operation, params) {
        try {
            const response = await api.post('/presigned-url', {
                operation,
                ...params,
            });
            return response.data;
        } catch (error) {
            console.error(`Failed to get ${operation} URL:`, error);

            throw error;
        }
    },

    async getUploadUrl(fileType, fileName, projectId) {
        return this.getPresignedUrl('upload', {
            fileType,
            fileName,
            projectId,
        });
    },

    async getDownloadUrl(fileKey) {
        return this.getPresignedUrl('download', {
            key: fileKey,
        });
    },

    // async deleteFile(fileKey) {
    //     try {
    //         await api.post('/presigned-url', {
    //             operation: 'delete',
    //             key: fileKey,
    //         });
    //         return true;
    //     } catch (error) {
    //         console.error('Failed to delete file:', error);

    //         throw error;
    //     }
    // },

    async deleteFileFromS3(fileKey) {
        try {
            await api.post('/presigned-url', {
                operation: 'delete',
                key: fileKey,
            });
            return true;
        } catch (error) {
            console.error('Failed to delete file from S3:', error);

            throw error;
        }
    },

    async uploadFileToS3(file, projectId, onProgress = null) {
        try {
            const contentType = file.type || getMimeType(file.name);
            const response = await api.post('/presigned-url', {
                operation: 'upload',
                fileType: contentType,
                fileName: file.name,
                projectId,
            });
            const { presignedUrl, fileUrl, key } = response.data;

            // Using XMLHttpRequest for upload progress
            if (onProgress) {
                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.upload.addEventListener('progress', event => {
                        if (event.lengthComputable) {
                            const percentComplete = (event.loaded / event.total) * 100;
                            onProgress(percentComplete);
                        }
                    });

                    xhr.addEventListener('load', () => {
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve();
                        } else {
                            reject(new Error('Upload failed'));
                        }
                    });
                    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
                    xhr.open('PUT', presignedUrl);
                    xhr.setRequestHeader('Content-Type', contentType);
                    // Remove any existing Authorization header for the S3 request
                    xhr.setRequestHeader('Access-Control-Allow-Origin', '*');
                    xhr.send(file);
                });
            } else {
                await axios.put(presignedUrl, file, {
                    headers: {
                        'Content-Type': contentType,
                        'Access-Control-Allow-Origin': '*',
                    },
                });
            }

            // Return both the S3 key and the URL for immediate use
            return {
                s3Key: key,
                s3Url: fileUrl,
                // Add downloadUrl for immediate access
                downloadUrl: (
                    await api.post('/presigned-url', {
                        operation: 'download',
                        key: key,
                    })
                ).data.downloadUrl,
            };
        } catch (error) {
            console.error('Failed to upload file to S3:', error);
            throw error;
        }
    },
    async downloadFileFromS3(fileKey) {
        try {
            const response = await api.post('/presigned-url', {
                operation: 'download',
                key: fileKey,
            });

            const { downloadUrl } = response.data;
            const fileResponse = await api2.get(downloadUrl, {
                responseType: 'blob',
            });
            return fileResponse.data;
        } catch (error) {
            console.error('Failed to download file from S3:', error);

            throw error;
        }
    },

    // async handleCompleteFileUpload(file, projectId, onProgress = null) {
    //     try {
    //         // 1. Get upload URL
    //         const { presignedUrl, fileUrl, key } = await this.getUploadUrl(file.type, file.name, projectId);
    //         await this.uploadFileToS3(presignedUrl, file, onProgress);
    //         return {
    //             s3Key: key,
    //             s3Url: fileUrl,
    //         };
    //     } catch (error) {
    //         console.error('Complete file upload process failed:', error);

    //         throw error;
    //     }
    // },

    // Collection

    async createProjectCollection(userId, projectsData, collectionData) {
        try {
            const response = await api.post('/collections', {
                userId,
                collectionData: collectionData,
                
                // projects: projectsData.map(project => ({
                //     type: project.type,
                //     details: {
                //         ...project.details,
                //         // Any other specific project details
                //     }
                // }))
                projects: projectsData
            });
            return response.data.data.body;
        } catch (error) {
            console.error('Failed to create project collection', error);
            throw error;
        }
    },
    async getCollectionDetails(collectionId) {
        try {
            const response = await api.get(`/getCollectionDetails/${collectionId}`);
            return response.data;
        } catch (error) {
            console.error('Failed to get collection details', error);
            throw error;
        }
    },
    async getCollectionData(collectionId) {
        try {
            // This endpoint should return minimal data needed for navigation:
            // - collection name
            // - list of projects with just id, name, and type
            const response = await api.get(`/collections/${collectionId}`);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch collection navigation data:', error);
            throw error;
        }
    },

    async getUserCollections(userId) {
        const response = await api.get(`/collections/user/${userId}`);
        return response;
    },

    async updateCollection(collectionId, updates) {
            
        try{
            const response = await api.put(`/collections/${collectionId}`, {
                body: {
                    ...updates,
                    updatedAt: Date.now()
                }
            });
            return response.data;
        }  catch (error) {
            console.error('Failed to update collection', error);
            throw error;
        }
    },

    async deleteCollection(collectionId) {
        try {
            const response = await api.delete(`/delete/collection/${collectionId}`);
            return response;
        } catch (error) {
            console.error('Failed to delete collection', error);
            throw error;
        }
    },

    async addProjectToCollection(collectionId, projectId) {
        const response = await api.post(`/collections/${collectionId}/projects`, {
            body: {
                projectId
            }
        });
        return response;
    },

    async removeProjectFromCollection(collectionId, projectId) {
        const response = await api.delete(`/collections/${collectionId}/${projectId}`);
        return response;
    },

    async uploadStartProjectConfig(jsonData, fileName = "startProjectConfig.json", businessId = null) {
        try {

            const currentBusinessId = businessId || AssetsConfig.getBusinessIdFromSubdomain();
            // Get presigned URL using the new uploadConfig operation
            const response = await api.post('/presigned-url', {
                operation: 'uploadConfigFile',
                fileType: 'application/json',
                businessId: currentBusinessId,
                fileName: fileName,
            });

            const { presignedUrl } = response.data;

            // Convert JSON data to string if it's an object
            const jsonContent = typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData);
            console.log(jsonContent.type, 'Uploading project config:', jsonContent);

            // Upload using axios
            await axios.put(presignedUrl, jsonContent, {
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                }
            });

            return true;
    
        } catch (error) {
            console.error('Failed to upload project config:', error);
            throw error;
        }
    },

    async uploadConfigJSON(jsonData, fileName = "startProjectConfig.json", businessId = null) {
        try {

            const currentBusinessId = businessId || AssetsConfig.getBusinessIdFromSubdomain();
            // Get presigned URL using the new uploadConfig operation
            const response = await api.post('/presigned-url', {
                operation: 'uploadConfigFile',
                fileType: 'application/json',
                businessId: currentBusinessId,
                fileName: fileName,
            });

            const { presignedUrl } = response.data;

            // Convert JSON data to string if it's an object
            const jsonContent = typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData);
            console.log(jsonContent.type, 'Uploading project config:', jsonContent);

            // Upload using axios
            await axios.put(presignedUrl, jsonContent, {
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                }
            });

            return true;
    
        } catch (error) {
            console.error('Failed to upload project config:', error);
            throw error;
        }
    },

    async uploadAssetImage(file, businessId = null) {
        try {
            const currentBusinessId = businessId || AssetsConfig.getBusinessIdFromSubdomain();
            const contentType = file.type || getMimeType(file.name);

            // Get presigned URL for asset upload
            const response = await api.post('/presigned-url', {
                operation: 'uploadConfigFile',
                fileType: contentType,
                businessId: currentBusinessId,
                fileName: `images/${file.name}`,
            });

            const { presignedUrl, fileUrl } = response.data;

            // Upload the file
            await axios.put(presignedUrl, file, {
                headers: {
                    'Content-Type': contentType,
                    'Access-Control-Allow-Origin': '*',
                }
            });

            return {
                url: fileUrl,
                fileName: file.name
            };
        } catch (error) {
            console.error('Failed to upload asset image:', error);
            throw error;
        }
    },

    async deleteAssetImage(imageUrl) {
        try {
            // Extract the key from the URL
            // The URL format is like: https://bucket.s3.region.amazonaws.com/assets/businessId/materials/filename
            const urlParts = imageUrl.split('/assets/');
            if (urlParts.length !== 2) {
                throw new Error('Invalid image URL format');
            }
            const key = `assets/${urlParts[1]}`;

            // Delete the file using the existing deleteFileFromS3 method
            await this.deleteFileFromS3(key);
            return true;
        } catch (error) {
            console.error('Failed to delete asset image:', error);
            throw error;
        }
    },

};
export default ProjectService;
